import {
  type ComponentType,
  createContext,
  type ElementType,
  type KeyboardEvent,
  type ReactNode,
  type SVGAttributes,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { noop } from "lodash";

import { MotionConfig } from "motion/react";

import {
  AnalyticsIcon,
  AssetsIcon,
  type BoxProps,
  Box,
  ChatIcon,
  CloseIcon,
  Column,
  FlowsIcon,
  IconButton,
  MenuItem,
  OverflowMenuIcon,
  PlusIcon,
  Popover,
  PopoverTrigger,
  Portal,
  ProjectsIcon,
  Row,
  Text,
  TextWithTooltip,
  Tooltip,
} from "@hightouch/htui";

import {
  AUTOMATIONS_PANEL,
  CHATS_PANEL,
  type CdpEntry,
  ampPanelItem,
  INSIGHTS_PANEL,
  isUnder,
  LIBRARY_PANEL,
  PROJECTS_PANEL,
  panelForPath,
  workspacePath,
} from "src/components/layout/nav/amp-nav-panels";
import {
  arrangeRootEntries,
  NAV_ROOT_SECTIONS,
  placeInSections,
  toggleHidden,
  useNavLayout,
} from "src/components/layout/nav/amp-nav-layout";
import { AmpSettingsLinks, Footer } from "src/components/layout/nav/footer";
import {
  CursorContextMenu,
  useCursorContextMenu,
} from "src/components/cursor-context-menu";
import {
  cachedNavIcon,
  crumbIcon,
  navIcon,
} from "src/components/layout/nav/nav-icon";
import { Icon as HomeIcon } from "src/components/layout/nav/icons/home";
import { MiniNavPeekContext } from "src/components/layout/nav/mini-nav/mini-nav-context";
import type { NavPlace } from "src/components/layout/nav/mini-nav/mini-nav-state";
import { CompactNavContext } from "src/components/layout/nav/compact-nav-context";
import { AmpNavItem, NavItem } from "src/components/layout/nav/nav-item";
import { NavHoverProvider } from "src/components/layout/nav/nav-hover";
import { NavHoverGroupContext } from "src/components/layout/nav/nav-hover-store";
import {
  focusNavTarget,
  NAV_MORE_SELECTOR,
  navEntrySelector,
} from "src/components/layout/nav/nav-focus";
import { NavGroupLabel } from "src/components/layout/nav/nav-group-label";
import {
  NavPopoverContentAndBody,
  PopoverHeader,
} from "src/components/layout/nav/nav-popovers";
import { isLinkActive, isSubPath } from "src/components/layout/nav/utils";
import {
  ExpandedNavSection,
  type NavSectionLink,
} from "src/components/layout/nav/nav-section";
import {
  AMP_CONTENT_LABEL,
  AMP_INSIGHTS_PATH,
  type AmpNavLink,
  useAmpNavLinks,
} from "src/components/layout/nav/sections/amp-nav-links";
import { GetStarted } from "src/components/layout/nav/sections/get-started";
import { Home } from "src/components/layout/nav/sections/home";
import { AGENT_CHATS_PATH } from "src/components/layout/nav/sections/hooks/agents";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import {
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";
import { RouterLink, useLocation, useNavigationType } from "src/router";
import {
  ampRootRows,
  isAutomationLink,
  PROJECTS_LINKS,
  useCdpEntries,
} from "src/components/layout/nav/use-reachable-nav";

/** Where a page under one of AMP's rows sits, for its crumb trail. */
type AmpPlace = {
  title: string;
  /** The row's first page, which its crumb links to. Automations has none, since its first page depends on what the viewer can reach. */
  href?: string;
  icon: ComponentType<SVGAttributes<SVGElement>>;
};

// Keyed as the nav keys its rows, which viewers' saved layouts hide them by.
const AMP_PLACES = new Map<string, AmpPlace>([
  [CHATS_PANEL, { title: "Chats", href: AGENT_CHATS_PATH, icon: ChatIcon }],
  [
    PROJECTS_PANEL,
    {
      title: TERMS.project.Plural,
      href: PROJECTS_PATHS.projects,
      icon: ProjectsIcon,
    },
  ],
  [
    LIBRARY_PANEL,
    {
      title: AMP_CONTENT_LABEL,
      href: PROJECTS_PATHS.libraryNew,
      icon: AssetsIcon,
    },
  ],
  [AUTOMATIONS_PANEL, { title: "Automations", icon: FlowsIcon }],
  [
    INSIGHTS_PANEL,
    { title: "Insights", href: AMP_INSIGHTS_PATH, icon: AnalyticsIcon },
  ],
]);

const MoreNavIcon = navIcon(OverflowMenuIcon);

/** The section open in the list: the one the URL opens, until the viewer opens another in place on this page. */
const useOpenSection = (urlSection: string) => {
  const { pathname, key } = useLocation();
  const navigationType = useNavigationType();
  const opensInPlace = useContext(NavDrillsInPlaceContext);
  const isPeeking = useContext(MiniNavPeekContext);
  const [chosen, setChosen] = useState<{
    pathname: string;
    section: string;
  } | null>(null);
  // Back and Forward drop the choice, so the open section follows the page they land on; a page changing only its query keeps it.
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    if (chosen && (navigationType === "POP" || chosen.pathname !== pathname)) {
      setChosen(null);
    }
  }
  // A choice made in place belongs to the peek or the sheet, so docking or closing the peek hands the list back to the page.
  const overlay = `${opensInPlace}:${isPeeking}`;
  const [lastOverlay, setLastOverlay] = useState(overlay);
  if (overlay !== lastOverlay) {
    setLastOverlay(overlay);
    if (chosen) setChosen(null);
  }
  const section = chosen?.pathname === pathname ? chosen.section : urlSection;
  const choose = (next: string) => setChosen({ pathname, section: next });
  return [section, choose] as const;
};

/** The section a pathname sits under, given the root's links: Chats holds Ad Studio's chats only while the nav lists it. */
const urlPanelFor = (
  pathname: string,
  entries: CdpEntry[],
  { lead }: { lead: AmpNavLink[] },
) =>
  panelForPath(
    pathname,
    entries,
    lead.some(({ href }) => href === AGENT_CHATS_PATH),
  );

/** Whether the nav is the peeked sidebar or the phone's sheet, where a section opened from its chevron stays open only while the overlay does. */
export const NavDrillsInPlaceContext = createContext(false);

/** The open section once its chevron closes it, which matches no section. */
const NO_SECTION = "";

const HOME_PATH = "/home";

/**
 * Where the page sits in the nav: its section, linked to the section's first page, then the row it lights in that section.
 * The mini-nav's trail starts here, so every page under a section names it.
 */
export const useNavPlace = (): NavPlace => {
  const { pathname, search } = useLocation();
  const entries = useCdpEntries();
  const links = useAmpNavLinks();
  const { lead, area, more } = links;
  const panel = urlPanelFor(pathname, entries, links);
  const path = workspacePath(pathname);
  const isHere = (link: NavSectionLink) =>
    isLinkActive(pathname, link.href, link.isSub, link.activePaths);
  const section =
    entries.find(({ title }) => title === panel) ??
    entries.find((entry) => entry.isLink && entry.links.some(isHere));
  if (section) {
    const sectionCrumb = {
      label: section.title,
      link: section.links[0]?.href,
      icon: crumbIcon(section.icon),
    };
    // A section of one row is that row, so its trail names it once.
    if (section.isLink) return { section: sectionCrumb };
    // A section of several rows only opens a panel, and its crumb's link is just its first row's page.
    const page = section.links.find(isHere);
    return page
      ? {
          section: sectionCrumb,
          item: { label: page.label, link: page.href },
          isItemCurrent: page.href === path,
          isSectionGroup: true,
        }
      : { section: sectionCrumb, isSectionGroup: true };
  }
  const ampPlace = AMP_PLACES.get(panel);
  if (ampPlace) {
    const item = ampPanelItem(panel, pathname, search);
    return {
      section: {
        label: ampPlace.title,
        link:
          panel === AUTOMATIONS_PANEL
            ? more.find(isAutomationLink)?.href
            : ampPlace.href,
        icon: ampPlace.icon,
      },
      item,
      isItemCurrent: item?.link?.split("?")[0] === path,
    };
  }
  // Home heads the nav on its own, and Inbox and the like are rows of their own with no section to sit under.
  if (isLinkActive(pathname, HOME_PATH, false)) {
    return {
      section: { label: "Home", link: HOME_PATH, icon: crumbIcon(HomeIcon) },
    };
  }
  const isHereOrUnder = (href: string) => isUnder(pathname, href);
  const row = [...lead, ...area, ...more].find(
    ({ href, activePaths }) =>
      isHereOrUnder(href) || activePaths?.some(isHereOrUnder),
  );
  return row
    ? { section: { label: row.label, link: row.href, icon: row.icon } }
    : {};
};

/** The AMP v2 nav: AMP's pages and the CDP's sections in one list, each section with pages opening them under its row. */
export const AmpNavSections = ({ onClick }: Pick<BoxProps, "onClick">) => {
  const { pathname } = useLocation();
  const entries = useCdpEntries();
  const urlPanel = urlPanelFor(pathname, entries, useAmpNavLinks());
  const [openSection, chooseSection] = useOpenSection(urlPanel);
  // Under half a row, and the list's bottom padding matches it, so the last row can always scroll clear of the fade.
  const scrollFade = useScrollFade(8);

  return (
    <CompactNavContext.Provider value>
      <Column
        as="nav"
        aria-label="Main navigation"
        flex={1}
        gap={2}
        justifyContent="space-between"
        minHeight={0}
        width="100%"
        // Too short for the list's least height and the footer, as at 200% zoom, the list and footer scroll as one.
        overflowY="auto"
        onClick={onClick}
      >
        <Box
          ref={scrollFade.ref}
          flex={1}
          // At least four rows show, however short the window.
          minHeight={48}
          cursor="default"
          overflowY="auto"
          sx={{
            ...scrollFade.sx,
            // Holds the scrollbar's space, so rows keep their width when a section opens past the fold.
            scrollbarGutter: "stable",
          }}
          onClick={onClick}
        >
          {/* Reduced motion also stills the rows' layout animation as a section opens above them. */}
          <MotionConfig reducedMotion="user">
            <NavHoverProvider>
              {/* Its own column, so the nav items' links don't stretch to the list's height. */}
              <Column
                data-nav-list
                gap={2}
                px={2}
                pt={1}
                pb={2}
                // The gaps between rows are empty space too.
                onClick={onClick}
              >
                <RootList
                  entries={entries}
                  urlPanel={urlPanel}
                  openSection={openSection}
                  onToggle={(next) =>
                    chooseSection(next === openSection ? NO_SECTION : next)
                  }
                />
              </Column>
            </NavHoverProvider>
          </MotionConfig>
        </Box>
        {/* Its own pill: the list's sits above the scroll fade, so the two never trade one. */}
        <NavHoverProvider>
          <Footer>
            <AmpSettingsLinks />
          </Footer>
        </NavHoverProvider>
      </Column>
    </CompactNavContext.Provider>
  );
};

type RootListProps = {
  entries: CdpEntry[];
  /** The section the page belongs to, which lights its row. */
  urlPanel: string;
  /** The section whose pages show under its row, or none. */
  openSection: string;
  /** Shows a section's pages, or hides them if they show. */
  onToggle: (section: string) => void;
};

const RootList = ({
  entries,
  urlPanel,
  openSection,
  onToggle,
}: RootListProps) => {
  const { pathname } = useLocation();
  // A row for one of AMP's pages that a section of the nav stands for, lit on every page under that section.
  const sectionRow = (key: string, link: AmpNavLink): RootEntry => ({
    key,
    fallbackSection: 0,
    hideable: {
      label: link.label,
      icon: cachedNavIcon(link.icon),
      href: link.href,
      isActive: urlPanel === key,
    },
    node: (
      <NavItem
        href={link.href}
        isActive={urlPanel === key}
        icon={cachedNavIcon(link.icon)}
        label={link.label}
        markerLabel={link.markerLabel}
      />
    ),
  });
  const pageRow = (link: AmpNavLink): RootEntry => ({
    key: link.href,
    fallbackSection: 0,
    hideable: {
      label: link.label,
      icon: cachedNavIcon(link.icon),
      href: link.href,
      isActive: isLinkActive(
        pathname,
        link.href,
        isSubPath(link.href),
        link.activePaths,
      ),
    },
    node: <AmpNavItem link={link} />,
  });
  const projectsRow: RootEntry = {
    key: PROJECTS_PANEL,
    fallbackSection: 0,
    hideable: {
      label: TERMS.project.Plural,
      icon: cachedNavIcon(ProjectsIcon),
      href: PROJECTS_PATHS.projects,
      isActive: urlPanel === PROJECTS_PANEL,
    },
    node: (
      <ProjectsEntry
        isOpen={openSection === PROJECTS_PANEL}
        isActive={urlPanel === PROJECTS_PANEL}
        onToggle={() => onToggle(PROJECTS_PANEL)}
      />
    ),
  };
  const automationsRow = (
    links: AmpNavLink[],
    first: AmpNavLink,
  ): RootEntry => ({
    key: AUTOMATIONS_PANEL,
    fallbackSection: 0,
    hideable: {
      label: "Automations",
      icon: cachedNavIcon(FlowsIcon),
      href: first.href,
      isActive: urlPanel === AUTOMATIONS_PANEL,
    },
    node: (
      <SectionEntry
        title="Automations"
        icon={cachedNavIcon(FlowsIcon)}
        links={links.map(({ href, label }) => ({
          href,
          label,
          // Two segments, so a link matches its own page, not every agent page.
          isSub: isSubPath(href),
        }))}
        isOpen={openSection === AUTOMATIONS_PANEL}
        isActive={urlPanel === AUTOMATIONS_PANEL}
        onToggle={() => onToggle(AUTOMATIONS_PANEL)}
      />
    ),
  });
  const cdpRow = (entry: CdpEntry, first: NavSectionLink): RootEntry => {
    const isSub = first.isSub ?? isSubPath(first.href);
    return {
      key: entry.title,
      fallbackSection: NAV_ROOT_SECTIONS.length - 1,
      hideable: {
        label: entry.title,
        icon: entry.icon,
        href: first.href,
        isActive:
          urlPanel === entry.title ||
          isLinkActive(pathname, first.href, isSub, first.activePaths),
      },
      node: entry.isLink ? (
        <NavItem
          isSub={isSub}
          activePaths={first.activePaths}
          href={first.href}
          icon={entry.icon}
          label={entry.title}
        />
      ) : (
        <SectionEntry
          title={entry.title}
          icon={entry.icon}
          links={entry.links}
          isOpen={openSection === entry.title}
          isActive={urlPanel === entry.title}
          onToggle={() => onToggle(entry.title)}
        />
      ),
    };
  };

  const rootEntries: RootEntry[] = [
    { key: "get-started", node: <GetStarted /> },
    { key: "home", node: <Home /> },
    ...ampRootRows(useAmpNavLinks(), entries).map(
      (row): RootEntry =>
        row.kind === "place"
          ? sectionRow(row.key, row.link)
          : row.kind === "page"
            ? pageRow(row.link)
            : row.kind === "projects"
              ? projectsRow
              : row.kind === "automations"
                ? automationsRow(row.links, row.first)
                : cdpRow(row.entry, row.first),
    ),
  ];

  const [layout, setLayout] = useNavLayout();
  const { visible, hidden, shownWhileActive, canHide } = arrangeRootEntries(
    placeInSections(rootEntries, NAV_ROOT_SECTIONS),
    layout,
  );
  // One section on show needs no label to set it apart.
  const showsSectionLabels =
    new Set(visible.flatMap(({ section }) => (section ? [section] : []))).size >
    1;
  const toggle = (key: string) => {
    const index = visible.findIndex((entry) => entry.key === key);
    const entry = visible[index];
    // The open page's row stays in the list when hidden, so focus can stay on it.
    const leavesList =
      entry !== undefined &&
      !shownWhileActive.has(key) &&
      !entry.hideable?.isActive;
    const next = visible.slice(index + 1).find(({ hideable }) => hideable);
    setLayout(toggleHidden(layout, key));
    // The hide or show button unmounts with its row, so focus moves to where the viewer was.
    focusNavTarget(
      !leavesList
        ? navEntrySelector(key)
        : next
          ? navEntrySelector(next.key)
          : NAV_MORE_SELECTOR,
      { onlyIfLost: true },
    );
  };

  return (
    <>
      {visible.map((entry, index) => {
        const label =
          showsSectionLabels &&
          entry.section !== visible[index - 1]?.section &&
          entry.section;
        return (
          <NavHoverGroupContext.Provider
            key={entry.key}
            value={showsSectionLabels ? entry.section : undefined}
          >
            {label && <NavGroupLabel>{label}</NavGroupLabel>}
            {entry.hideable ? (
              <HideableEntry
                entryKey={entry.key}
                isHidden={shownWhileActive.has(entry.key)}
                canHide={canHide}
                onToggle={() => toggle(entry.key)}
              >
                {entry.node}
              </HideableEntry>
            ) : (
              entry.node
            )}
          </NavHoverGroupContext.Provider>
        );
      })}
      {hidden.length > 0 && (
        // More has no label of its own, so it joins the last group.
        <NavHoverGroupContext.Provider
          value={showsSectionLabels ? visible.at(-1)?.section : undefined}
        >
          <MoreMenu
            entries={hidden.flatMap(({ key, hideable }) =>
              hideable ? [{ key, ...hideable }] : [],
            )}
            onShow={toggle}
          />
        </NavHoverGroupContext.Provider>
      )}
    </>
  );
};

/** What More lists for a root entry while the viewer has it hidden. */
type HideableMeta = {
  label: string;
  icon: ElementType;
  href: string;
  isActive: boolean;
};

/** One root entry, keyed for the viewer's layout; only entries with `hideable` can be hidden. */
type RootEntry = {
  key: string;
  node: ReactNode;
  hideable?: HideableMeta;
  /** The index in `NAV_ROOT_SECTIONS` an entry no section lists falls into; Home and Get started have none and sit on top. */
  fallbackSection?: number;
};

type HideableEntryProps = {
  entryKey: string;
  /** Hidden by the viewer, but on show while its page is open. */
  isHidden: boolean;
  canHide: boolean;
  onToggle: () => void;
  children: ReactNode;
};

/** A root entry whose right-click menu moves it into More, or back out. */
const HideableEntry = ({
  entryKey,
  isHidden,
  canHide,
  onToggle,
  children,
}: HideableEntryProps) => {
  const {
    contextMenuTriggerProps,
    contextMenuProps,
    isContextMenuOpen,
    openContextMenuAt,
  } = useCursorContextMenu(true);
  const menuItemRef = useRef<HTMLButtonElement>(null);

  // A menu opened already open never focuses its item, which the keyboard needs to reach it.
  useEffect(() => {
    if (!isContextMenuOpen) return;
    const id = setTimeout(() => menuItemRef.current?.focus());
    return () => clearTimeout(id);
  }, [isContextMenuOpen]);

  // The last item on show keeps its button, disabled, so the reason it can't hide is right there.
  const canToggle = isHidden || canHide;
  const toggleLabel = isHidden
    ? "Show in sidebar"
    : canHide
      ? "Hide from sidebar"
      : "Keep at least one item in the sidebar";

  // The keyboard's way to the right-click menu, which a focused row otherwise never opens.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      (event.key !== "ContextMenu" &&
        !(event.shiftKey && event.key === "F10")) ||
      // Portaled menus and popovers bubble here through React.
      !event.currentTarget.contains(event.target as Node) ||
      // A menu holding only a disabled item could not take focus, so nothing could close it.
      !canToggle
    ) {
      return;
    }
    event.preventDefault();
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    openContextMenuAt({ x: rect.left, y: rect.bottom });
  };

  const closeContextMenu = () => {
    contextMenuProps.onClose();
    // The menu's hidden button unmounts with it, so focus would fall to the body.
    focusNavTarget(navEntrySelector(entryKey), { onlyIfLost: true });
  };

  return (
    <Box
      data-nav-entry={entryKey}
      position="relative"
      onKeyDown={handleKeyDown}
      {...contextMenuTriggerProps}
    >
      {children}
      {/* Portaled, so the menu stacks over the page rather than inside the nav's own stacking context. */}
      {isContextMenuOpen && (
        <Portal>
          <CursorContextMenu
            {...contextMenuProps}
            zIndex="popover"
            onClose={closeContextMenu}
          >
            <MenuItem
              ref={menuItemRef}
              icon={isHidden ? PlusIcon : CloseIcon}
              isDisabled={!canToggle}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}
            >
              {isHidden ? "Show in sidebar" : "Hide from sidebar"}
            </MenuItem>
            {!canToggle && (
              <Box px={3} pb={1}>
                <Text size="sm" color="text.tertiary">
                  {toggleLabel}
                </Text>
              </Box>
            )}
          </CursorContextMenu>
        </Portal>
      )}
    </Box>
  );
};

type MoreMenuProps = {
  entries: Array<HideableMeta & { key: string }>;
  onShow: (key: string) => void;
};

/** The hidden root entries, one click away, each with a way back into the nav. */
const MoreMenu = ({ entries, onShow }: MoreMenuProps) => {
  return (
    // Marks the trigger, so focus lands on it after the last row above is hidden.
    <Box data-nav-more>
      <Popover isLazy placement="right-start" offset={[0, 16]}>
        {({ onClose }) => (
          <>
            <PopoverTrigger>
              <NavItem icon={MoreNavIcon} label="More" onClick={noop} />
            </PopoverTrigger>
            <Portal>
              <NavPopoverContentAndBody
                width={64}
                ariaLabel="Hidden from sidebar"
              >
                <PopoverHeader title="Hidden from sidebar" />
                {entries.map((entry) => (
                  <HiddenEntryRow
                    key={entry.key}
                    entry={entry}
                    onNavigate={onClose}
                    onShow={() => onShow(entry.key)}
                  />
                ))}
              </NavPopoverContentAndBody>
            </Portal>
          </>
        )}
      </Popover>
    </Box>
  );
};

type HiddenEntryRowProps = {
  entry: HideableMeta;
  onNavigate: () => void;
  onShow: () => void;
};

const HiddenEntryRow = ({ entry, onNavigate, onShow }: HiddenEntryRowProps) => (
  // The link and the show button are siblings, so neither nests inside the other.
  <Row align="center" gap={1} mx={1.5}>
    <Row
      as={RouterLink}
      to={entry.href}
      preload="intent"
      flex={1}
      minWidth={0}
      align="center"
      gap={2}
      h={8}
      px={2}
      borderRadius="lg"
      fontWeight="medium"
      color="text.primary"
      transitionProperty="background-color"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      _hover={{ bg: "base.background" }}
      _focusVisible={{ outline: "none", boxShadow: "outline" }}
      onClick={onNavigate}
    >
      <Box as={entry.icon} flexShrink={0} color="text.tertiary" />
      <TextWithTooltip size="md" fontWeight="medium" color="inherit">
        {entry.label}
      </TextWithTooltip>
    </Row>
    <Tooltip message="Show in sidebar" placement="right">
      <IconButton
        aria-label={`Show ${entry.label} in sidebar`}
        icon={PlusIcon}
        size="sm"
        variant="tertiary"
        onClick={onShow}
      />
    </Tooltip>
  </Row>
);

type SectionEntryProps = {
  title: string;
  icon: ElementType;
  links: NavSectionLink[];
  isOpen: boolean;
  /** Whether the page sits under this section, which colors its row. */
  isActive: boolean;
  onToggle: () => void;
};

/** A section's row, which goes to its first page, with a chevron that shows or hides its pages under it. */
const SectionEntry = ({
  title,
  icon,
  links,
  isOpen,
  isActive,
  onToggle,
}: SectionEntryProps) => (
  <ExpandedNavSection
    title={title}
    icon={icon}
    validLinks={links}
    defaultLink={links[0]}
    isOpen={isOpen}
    isActiveSection={isActive}
    onToggle={onToggle}
  />
);

/** Projects' row: the overview and Tasks. */
const ProjectsEntry = ({
  isOpen,
  isActive,
  onToggle,
}: Omit<SectionEntryProps, "title" | "icon" | "links">) => {
  const { pathname } = useLocation();
  const links: NavSectionLink[] = PROJECTS_LINKS.map((link) =>
    link.href === PROJECTS_PATHS.projects
      ? {
          ...link,
          // Only the list itself, so a project's page lights none of the rows.
          isActive: workspacePath(pathname) === PROJECTS_PATHS.projects,
        }
      : link,
  );
  return (
    <SectionEntry
      title={TERMS.project.Plural}
      icon={cachedNavIcon(ProjectsIcon)}
      links={links}
      isOpen={isOpen}
      isActive={isActive}
      onToggle={onToggle}
    />
  );
};
